// Booking.tsx
import React, { useEffect, useState } from 'react';
import '../styles/Booking.css';
import SEO from '../components/SEO';

// Define window interface with Elfsight
declare global {
  interface Window {
    ElefsightApps?: {
      reload: () => void;
    };
  }
}

const Booking: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Booking page schema for rich results
  const bookingSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "VitalBalanceHormones",
    "url": "https://vitalbalancehormones.com/booking",
    "logo": "https://vitalbalancehormones.com/logo512.png",
    "telephone": "+17207735733",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "8906 W Bowles Ave, Suite 110",
      "addressLocality": "Littleton",
      "addressRegion": "CO",
      "postalCode": "80123",
      "addressCountry": "US"
    },
    "potentialAction": {
      "@type": "ReserveAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://vitalbalancehormones.com/booking",
        "inLanguage": "en-US",
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/IOSPlatform", 
          "http://schema.org/AndroidPlatform"
        ]
      },
      "result": {
        "@type": "Reservation",
        "name": "Bioidentical Hormone Consultation"
      }
    }
  };

  // Load Elfsight script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://static.elfsight.com/platform/platform.js";
    script.async = true;
    script.onload = () => setIsLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Initialize Elfsight widget with a slight delay
  useEffect(() => {
    if (isLoaded) {
      const timer = setTimeout(() => {
        if (window.ElefsightApps) {
          window.ElefsightApps.reload();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoaded]);

  return (
    <div className="booking-page">
      <SEO 
        title="Schedule a Consultation | VitalBalanceHormones Bioidentical Hormone Therapy"
        description="Book your bioidentical hormone therapy consultation today. In-person and virtual appointments available with our hormone specialists. Start your wellness journey now."
        keywords="hormone therapy appointment, schedule bioidentical hormone consultation, book BHRT consultation, hormone specialist appointment, hormone therapy booking"
        url="/booking"
        schema={bookingSchema}
      />
      
      {/* Hero Section */}
      <section className="booking-hero">
        <div className="booking-hero-overlay"></div>
        <div className="booking-hero-content">
          <h1 className="booking-title">Schedule Your Consultation</h1>
          <p className="booking-subtitle">
            Take the first step toward hormonal balance and renewed vitality
          </p>
          <a href="#calendar" className="booking-cta-button">
            Book Now
          </a>
        </div>
      </section>

      {/* Main Content Section */}
      <div className="booking-content">
        {/* Appointment Info */}
        <section className="booking-info">
          <div className="booking-info-container">
            <div className="booking-info-text">
              <h2>What to Expect</h2>
              <p>
                Your initial consultation is comprehensive and typically lasts about 60 minutes. 
                We'll review your health history, current symptoms, and wellness goals before 
                recommending appropriate testing to assess your hormone levels.
              </p>
              
              <div className="booking-features">
                <div className="booking-feature">
                  <div className="booking-feature-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="booking-feature-text">
                    <h3>60-Minute Consultation</h3>
                    <p>In-depth discussion of your health history and symptoms</p>
                  </div>
                </div>
                
                <div className="booking-feature">
                  <div className="booking-feature-icon">
                    <i className="fas fa-video"></i>
                  </div>
                  <div className="booking-feature-text">
                    <h3>Flexible Scheduling</h3>
                    <p>In-person and telehealth appointments available</p>
                  </div>
                </div>
                
                <div className="booking-feature">
                  <div className="booking-feature-icon">
                    <i className="fas fa-credit-card"></i>
                  </div>
                  <div className="booking-feature-text">
                    <h3>Insurance Accepted</h3>
                    <p>We work with most major insurance providers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Booking Section - Widget Only */}
        <section id="calendar" className="booking-section">
          <div className="booking-container">
            <div className="booking-content-wrapper">
              {/* Elfsight Calendar Widget */}
              <div className={`elfsight-wrapper ${isLoaded ? 'loaded' : ''}`}>
                <div className="elfsight-app-81bc5350-48c9-4aac-9667-90801c9c7d96" data-elfsight-app-lazy></div>
                {!isLoaded && (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading appointment calendar...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="booking-process">
          <div className="booking-info-container">
            <h2>Our Booking Process</h2>
            <div className="process-steps">
              <div className="process-step">
                <div className="process-step-number">1</div>
                <div className="process-step-content">
                  <h3>Schedule Online</h3>
                  <p>Select your preferred date and time using our convenient online scheduler.</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-step-number">2</div>
                <div className="process-step-content">
                  <h3>Confirmation</h3>
                  <p>Receive an immediate confirmation email with appointment details and preparation instructions.</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-step-number">3</div>
                <div className="process-step-content">
                  <h3>Reminder</h3>
                  <p>Get a friendly reminder 24 hours before your appointment.</p>
                </div>
              </div>
              
              <div className="process-step">
                <div className="process-step-number">4</div>
                <div className="process-step-content">
                  <h3>Consultation</h3>
                  <p>Meet with our specialist and begin your journey to hormonal balance.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="booking-faqs">
          <div className="booking-info-container">
            <h2>Frequently Asked Questions</h2>
            
            <div className="faq-grid">
              <div className="faq-item">
                <h3>What should I bring to my first appointment?</h3>
                <p>
                  Please bring your ID, insurance card, a list of current medications, and any recent 
                  lab work or medical records related to your hormonal health.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>How long does the initial consultation take?</h3>
                <p>
                  Your initial consultation typically lasts about 60 minutes to allow time for a thorough 
                  discussion of your symptoms, health history, and treatment options.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Do you offer virtual consultations?</h3>
                <p>
                  Yes, we offer secure HIPAA-compliant telehealth appointments for both initial consultations 
                  and follow-up visits for your convenience.
                </p>
              </div>
              
              <div className="faq-item">
                <h3>Will insurance cover my appointment?</h3>
                <p>
                  We accept most major insurance plans. Coverage varies by provider and plan. Our staff 
                  will help verify your benefits before your appointment.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Company Expertise & Credentials Section */}
        <section className="booking-expertise-section">
          <div className="expertise-container">
            <div className="expertise-header">
              <h2>Why Choose VitalBalanceHormones for Your Hormone Health</h2>
              <p className="expertise-intro">
                Experience the difference of working with specialized hormone therapy experts who combine 
                advanced medical training with personalized, compassionate care.
              </p>
            </div>

            <div className="expertise-grid">
              {/* Professional Credentials */}
              <div className="expertise-block">
                <h3>Board-Certified Expertise</h3>
                <div className="credentials-list">
                  <div className="credential-item">
                    <i className="icon-certificate credential-icon"></i>
                    <div className="credential-content">
                      <h4>Kelly Buckley, PA-C</h4>
                      <p>Certified Physician Assistant specializing in women's hormone health with extensive training in bioidentical hormone replacement therapy</p>
                    </div>
                  </div>
                  <div className="credential-item">
                    <i className="icon-graduation-cap credential-icon"></i>
                    <div className="credential-content">
                      <h4>Advanced Hormone Training</h4>
                      <p>Specialized education in functional medicine, hormone optimization, and integrative wellness approaches</p>
                    </div>
                  </div>
                  <div className="credential-item">
                    <i className="icon-award credential-icon"></i>
                    <div className="credential-content">
                      <h4>Continuing Education</h4>
                      <p>Ongoing professional development in the latest hormone therapy research and treatment protocols</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Treatment Philosophy */}
              <div className="expertise-block">
                <h3>Our Treatment Philosophy</h3>
                <div className="philosophy-points">
                  <div className="philosophy-item">
                    <h4>Education-First Approach</h4>
                    <p>We believe informed patients make the best decisions. Every consultation includes comprehensive education about your hormone health and treatment options.</p>
                  </div>
                  <div className="philosophy-item">
                    <h4>Personalized Medicine</h4>
                    <p>No two women are alike. We create individualized treatment plans based on your unique symptoms, lab results, and health goals.</p>
                  </div>
                  <div className="philosophy-item">
                    <h4>Root Cause Focus</h4>
                    <p>Rather than just treating symptoms, we identify and address the underlying hormonal imbalances affecting your health and quality of life.</p>
                  </div>
                  <div className="philosophy-item">
                    <h4>Integrative Wellness</h4>
                    <p>Hormone therapy is most effective when combined with lifestyle optimization including nutrition, exercise, stress management, and sleep hygiene.</p>
                  </div>
                </div>
              </div>

              {/* Advanced Testing & Monitoring */}
              <div className="expertise-block">
                <h3>Comprehensive Testing & Monitoring</h3>
                <div className="testing-features">
                  <div className="testing-category">
                    <h4>Advanced Hormone Panels</h4>
                    <ul>
                      <li>Complete sex hormone analysis (estrogen, progesterone, testosterone)</li>
                      <li>Thyroid function testing (TSH, T3, T4, reverse T3)</li>
                      <li>Adrenal health assessment (cortisol, DHEA)</li>
                      <li>Metabolic markers and nutrient status</li>
                    </ul>
                  </div>
                  <div className="testing-category">
                    <h4>Specialized Diagnostics</h4>
                    <ul>
                      <li>DUTCH hormone testing for detailed hormone metabolism</li>
                      <li>Food sensitivity and inflammatory marker testing</li>
                      <li>Genetic testing for hormone processing variations</li>
                      <li>Comprehensive metabolic panels</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Safety & Quality Standards */}
              <div className="expertise-block">
                <h3>Safety & Quality Standards</h3>
                <div className="safety-standards">
                  <div className="standard-item">
                    <i className="icon-shield-check standard-icon"></i>
                    <div className="standard-content">
                      <h4>FDA-Approved Bioidentical Hormones</h4>
                      <p>We use only the highest quality, FDA-approved bioidentical hormones from certified compounding pharmacies</p>
                    </div>
                  </div>
                  <div className="standard-item">
                    <i className="icon-microscope standard-icon"></i>
                    <div className="standard-content">
                      <h4>Regular Monitoring Protocols</h4>
                      <p>Systematic follow-up testing and dose adjustments to ensure optimal hormone levels and treatment safety</p>
                    </div>
                  </div>
                  <div className="standard-item">
                    <i className="icon-heart-pulse standard-icon"></i>
                    <div className="standard-content">
                      <h4>Evidence-Based Medicine</h4>
                      <p>Treatment protocols based on the latest peer-reviewed research and clinical guidelines for hormone therapy</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Patient-Centered Care */}
              <div className="expertise-block">
                <h3>Patient-Centered Care Experience</h3>
                <div className="care-features">
                  <div className="care-feature">
                    <h4>Comprehensive Consultations</h4>
                    <p>Extended appointment times allow for thorough evaluation and detailed discussion of your health concerns and treatment goals.</p>
                  </div>
                  <div className="care-feature">
                    <h4>Accessible Communication</h4>
                    <p>Direct access to your provider for questions and concerns throughout your treatment journey.</p>
                  </div>
                  <div className="care-feature">
                    <h4>Flexible Appointment Options</h4>
                    <p>In-person and telehealth consultations available to accommodate your schedule and preferences.</p>
                  </div>
                  <div className="care-feature">
                    <h4>Ongoing Support</h4>
                    <p>Regular follow-up appointments and monitoring to track your progress and optimize your treatment plan.</p>
                  </div>
                </div>
              </div>

              {/* Treatment Outcomes */}
              <div className="expertise-block">
                <h3>Expected Treatment Outcomes</h3>
                <div className="outcomes-grid">
                  <div className="outcome-category">
                    <h4>Physical Health Improvements</h4>
                    <ul>
                      <li>Increased energy and reduced fatigue</li>
                      <li>Better sleep quality and duration</li>
                      <li>Improved metabolism and weight management</li>
                      <li>Enhanced skin, hair, and nail health</li>
                      <li>Stronger bones and muscle mass</li>
                    </ul>
                  </div>
                  <div className="outcome-category">
                    <h4>Mental & Emotional Wellness</h4>
                    <ul>
                      <li>Improved mood stability and emotional balance</li>
                      <li>Enhanced mental clarity and focus</li>
                      <li>Reduced anxiety and depression symptoms</li>
                      <li>Better stress management and resilience</li>
                      <li>Increased confidence and motivation</li>
                    </ul>
                  </div>
                  <div className="outcome-category">
                    <h4>Quality of Life Enhancement</h4>
                    <ul>
                      <li>Restored libido and sexual satisfaction</li>
                      <li>Improved relationships and social engagement</li>
                      <li>Enhanced work performance and productivity</li>
                      <li>Greater enjoyment of daily activities</li>
                      <li>Overall sense of vitality and well-being</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Mission Statement */}
            <div className="mission-statement">
              <h3>Our Commitment to Your Health</h3>
              <p>
                At VitalBalanceHormones, we are dedicated to empowering women through evidence-based hormone therapy 
                that restores balance, enhances vitality, and improves quality of life. Our comprehensive approach 
                combines cutting-edge medical science with compassionate, personalized care to help you achieve 
                optimal hormonal health and renewed confidence in your body.
              </p>
              <div className="mission-highlights">
                <span className="mission-highlight">Personalized Treatment Plans</span>
                <span className="mission-highlight">Evidence-Based Medicine</span>
                <span className="mission-highlight">Comprehensive Care</span>
                <span className="mission-highlight">Patient Education Focus</span>
                <span className="mission-highlight">Ongoing Support</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Booking;